import { useEffect, useState } from 'react';

import { CMSSection } from '@/cms/CMSSection';
import { BoutiquesList } from '@/components/BoutiquesList';
import { Container } from '@/components/Container';
import { FindAStore } from '@/components/FindAStore';
import type { ContentfulPlaceholderStoresFragment } from '@/graphql/contentful/api';
import { usePosition } from '@/hooks/usePosition';
import type { StoreHit } from '@/integrations/algolia/types';
import { generateDataTestId } from '@/lib/tests';
import { useRegionProvider } from '@/providers/RegionProvider';
import { listingActions } from '@/states/listingState';

import { makeQueryString } from './helpers';

interface StoresResultsProps {
  info: ContentfulPlaceholderStoresFragment;
  stores: StoreHit[];
}

export const Stores = (props: ContentfulPlaceholderStoresFragment) => {
  const {
    country: { code },
  } = useRegionProvider();
  const { position } = usePosition();
  const aroundLatLng = position
    ? `${position.coords.latitude}, ${position.coords.longitude}`
    : undefined;
  const [stores, setStores] = useState<StoreHit[]>([]);

  useEffect(() => {
    listingActions.updateListingType('STORE');

    const fetchStores = async () => {
      const isGeolocationGranted =
        (
          await navigator.permissions.query({
            name: 'geolocation',
          })
        ).state === 'granted';

      const query = makeQueryString({
        countryCode: code,
        latLng: aroundLatLng,
        mode: props.type,
        numberOfStores: `${props.numberOfStores}`,
        boutiqueReferenceKeys: props.boutiqueReferenceKeys,
        webVisible: true,
      });

      if (props.type === 'Default' && isGeolocationGranted && !aroundLatLng) {
        return;
      }
      const result = await fetch(`/api/boutiques/?${query}`);
      const stores = await result.json();
      setStores(stores);
    };
    fetchStores();
  }, [aroundLatLng, code]);

  const [isInsightsReady, setIsInsightsReady] = useState(false);

  useEffect(() => {
    setIsInsightsReady(true);
  }, []);

  if (!isInsightsReady) {
    return null;
  }

  return (
    <CMSSection
      {...generateDataTestId('stores', 'section')}
      data-test-id='StoresSection'
    >
      <Container>
        <StoresResults info={props} stores={stores}></StoresResults>
      </Container>
    </CMSSection>
  );
};

export const StoresResults = ({ info, stores }: StoresResultsProps) => {
  if (!stores.length) {
    return (
      <FindAStore
        cardType='noResult'
        data-test-id='StoresResults-noResultCard'
        info={info}
        noStoreResults={true}
      />
    );
  }

  return (
    <BoutiquesList
      data-test-id='StoresResults-BoutiquesList'
      info={info}
      stores={stores}
    ></BoutiquesList>
  );
};
